import React, { useState, useMemo, useEffect } from 'react';
import { Message, Modal, Button } from 'semantic-ui-react';
import { PageContent, PageSection } from 'common/styles/page';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { StyledAddButton } from 'common/components/button/StyledButton';
import { metricsApi, MetricList } from '../api/metricsApi';
import { assessmentPeriodMetricsApi, AssessmentPeriodMetricList } from '../api/assessmentPeriodMetricsApi';
import { MetricList as MetricListComponent, ValuesFilterConfig, DefinitionsFilterConfig } from './MetricList';
import { MetricForm } from './MetricForm';
import { MetricImport } from './MetricImport';
import { MetricType } from './MetricTypeSelector';
import { useToasts } from 'common/toast';
import useFeatures from 'common/hooks/useFeatures';
import { useLazyExportMetricsQuery } from '../api/metricsApi';
import { useLazyExportAssessmentPeriodMetricsQuery } from '../api/assessmentPeriodMetricsApi';
import {isCurrentUserStaffOrSuperAdmin} from 'common/selectors/auth';
import { useSelector } from 'react-redux';
import { useApiPortalV1OrganizationListQuery } from 'store/generated/portal/organizations';
import {
  useGetTeamsByOrganizationQuery,
  useGetAssessmentPeriodsByOrganizationQuery,
  useGetMetricDefinitionsLookupQuery,
} from '../api/lookupApi';

// Base metric type options (standard types)
const METRIC_TYPE_OPTIONS = [
  { key: 'all', text: 'All Types', value: undefined },
  { key: MetricType.NPS, text: 'Net Promoter Score (NPS)', value: MetricType.NPS },
  { key: MetricType.CSAT, text: 'Customer Satisfaction (CSAT)', value: MetricType.CSAT },
  { key: MetricType.CUSTOM_KPI, text: 'Custom KPI', value: MetricType.CUSTOM_KPI },
  { key: MetricType.GENERIC, text: 'Generic Metric', value: MetricType.GENERIC },
  { key: MetricType.PILLAR, text: 'Pillar', value: MetricType.PILLAR },
  { key: MetricType.OUTCOME, text: 'Outcome', value: MetricType.OUTCOME },
] as const;

type TabType = 'definitions' | 'values';

export const PerformanceMetricsManager: React.FC = () => {
  const { addToast } = useToasts();
  const isStaff = useSelector(isCurrentUserStaffOrSuperAdmin);

  const [activeTab, setActiveTab] = useState<TabType>('definitions');

  // Filters for metric definitions
  const [metricTypeFilter, setMetricTypeFilter] = useState<number | undefined>(undefined);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);

  // Filters for period values - Organization is the primary filter
  const [organizationFilterValues, setOrganizationFilterValues] = useState<number | undefined>(undefined);
  const [assessmentPeriodFilter, setAssessmentPeriodFilter] = useState<number | undefined>(undefined);
  const [teamFilterValues, setTeamFilterValues] = useState<number | null | undefined>(undefined);
  const [metricIdFilter, setMetricIdFilter] = useState<number | undefined>(undefined);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [metricToEdit, setMetricToEdit] = useState<MetricList | AssessmentPeriodMetricList | null>(null);
  const [metricToDelete, setMetricToDelete] = useState<MetricList | AssessmentPeriodMetricList | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Organizations list - always loaded
  const { data: organizations = [] } = useApiPortalV1OrganizationListQuery();

  // Organization-scoped lookups - only fetch when an organization is selected
  const { data: scopedTeams = [], isLoading: isLoadingTeams } = useGetTeamsByOrganizationQuery(
    { organization_id: organizationFilterValues! },
    { skip: !organizationFilterValues }
  );

  const { data: scopedAssessmentPeriods = [], isLoading: isLoadingPeriods } = useGetAssessmentPeriodsByOrganizationQuery(
    { organization_id: organizationFilterValues! },
    { skip: !organizationFilterValues }
  );

  const { data: scopedMetrics = [] } = useGetMetricDefinitionsLookupQuery();

  // Reset dependent filters when organization changes
  useEffect(() => {
    // When organization changes, reset all dependent filters
    setAssessmentPeriodFilter(undefined);
    setTeamFilterValues(undefined);
    setMetricIdFilter(undefined);
  }, [organizationFilterValues]);
  
  // Query for metric definitions
  const { data: metricsResponse, isLoading: isLoadingDefinitions, error: definitionsError, refetch: refetchDefinitions } = metricsApi.useGetMetricsQuery({
    metric_type: metricTypeFilter,
    is_active: isActiveFilter,
  });
  
  // Query for assessment period metrics
  const { data: periodMetricsResponse, isLoading: isLoadingValues, error: valuesError, refetch: refetchValues } = assessmentPeriodMetricsApi.useGetAssessmentPeriodMetricsQuery({
    assessment_period_id: assessmentPeriodFilter,
    metric_id: metricIdFilter,
    organization_id: organizationFilterValues,
    team_id: teamFilterValues,
  });
  
  // Ensure metrics is always an array
  const metricDefinitions = useMemo(() => {
    if (!metricsResponse) return [];
    if (Array.isArray(metricsResponse)) return metricsResponse;
    if (metricsResponse && typeof metricsResponse === 'object' && 'results' in metricsResponse) {
      return Array.isArray((metricsResponse as any).results) ? (metricsResponse as any).results : [];
    }
    if (metricsResponse && typeof metricsResponse === 'object' && 'data' in metricsResponse) {
      return Array.isArray((metricsResponse as any).data) ? (metricsResponse as any).data : [];
    }
    return [];
  }, [metricsResponse]);
  
  // Ensure period metrics is always an array
  const periodMetrics = useMemo(() => {
    if (!periodMetricsResponse) return [];
    if (Array.isArray(periodMetricsResponse)) return periodMetricsResponse;
    if (periodMetricsResponse && typeof periodMetricsResponse === 'object' && 'results' in periodMetricsResponse) {
      return Array.isArray((periodMetricsResponse as any).results) ? (periodMetricsResponse as any).results : [];
    }
    if (periodMetricsResponse && typeof periodMetricsResponse === 'object' && 'data' in periodMetricsResponse) {
      return Array.isArray((periodMetricsResponse as any).data) ? (periodMetricsResponse as any).data : [];
    }
    return [];
  }, [periodMetricsResponse]);

  const [deleteMetric] = metricsApi.useDeleteMetricMutation();
  const [deletePeriodMetric] = assessmentPeriodMetricsApi.useDeleteAssessmentPeriodMetricMutation();
  const [exportMetrics] = useLazyExportMetricsQuery();
  const [exportPeriodMetrics] = useLazyExportAssessmentPeriodMetricsQuery();
  
  // Determine which data to use based on active tab
  const isLoading = activeTab === 'definitions' ? isLoadingDefinitions : isLoadingValues;
  const error = activeTab === 'definitions' ? definitionsError : valuesError;
  const metrics = activeTab === 'definitions' ? metricDefinitions : periodMetrics;
  const refetch = activeTab === 'definitions' ? refetchDefinitions : refetchValues;

  // NOTE: All hooks must be called before any conditional returns
  // Build filter options from scoped data
  const organizationOptions = useMemo(() => [
    { key: 'all', text: 'Select Organization', value: undefined },
    ...organizations.map((org: any) => ({
      key: org.id,
      text: org.name,
      value: org.id,
    })),
  ], [organizations]);

  const assessmentPeriodOptions = useMemo(() => [
    { key: 'all', text: 'All Periods', value: undefined },
    ...[...scopedAssessmentPeriods]
      .sort((a, b) => {
        const dateA = a.start_date_time ? new Date(a.start_date_time).getTime() : 0;
        const dateB = b.start_date_time ? new Date(b.start_date_time).getTime() : 0;
        return dateB - dateA;
      })
      .map(period => ({
        key: period.id,
        text: period.name || `Period ${period.id}`,
        value: period.id,
      })),
  ], [scopedAssessmentPeriods]);

  const teamOptions = useMemo(() => [
    { key: 'all', text: 'All Teams', value: undefined },
    { key: 'org', text: 'Org Level Only', value: 'org' },
    ...scopedTeams.map((team: any) => ({
      key: team.id,
      text: team.name,
      value: team.id,
    })),
  ], [scopedTeams]);

  const metricOptions = useMemo(() => [
    { key: 'all', text: 'All Metrics', value: undefined },
    ...scopedMetrics.map((m) => ({
      key: m.id,
      text: `${m.metric_name}${m.metric_label ? ` (${m.metric_label})` : ''}`,
      value: m.id,
    })),
  ], [scopedMetrics]);

  // Clear filters handlers
  const handleClearValuesFilters = () => {
    setOrganizationFilterValues(undefined);
    setAssessmentPeriodFilter(undefined);
    setTeamFilterValues(undefined);
    setMetricIdFilter(undefined);
  };

  const handleClearDefinitionsFilters = () => {
    setMetricTypeFilter(undefined);
    setIsActiveFilter(undefined);
  };

  // Build filter configurations for the MetricList component
  const valuesFilterConfig: ValuesFilterConfig = useMemo(() => ({
    organizationOptions,
    assessmentPeriodOptions,
    teamOptions,
    metricOptions,
    organizationValue: organizationFilterValues,
    assessmentPeriodValue: assessmentPeriodFilter,
    teamValue: teamFilterValues,
    metricValue: metricIdFilter,
    onOrganizationChange: setOrganizationFilterValues,
    onAssessmentPeriodChange: setAssessmentPeriodFilter,
    onTeamChange: setTeamFilterValues,
    onMetricChange: setMetricIdFilter,
    onClearFilters: handleClearValuesFilters,
    isOrganizationDisabled: false,
    isAssessmentPeriodDisabled: !organizationFilterValues,
    isTeamDisabled: !organizationFilterValues,
    isMetricDisabled: !organizationFilterValues,
    isLoadingOptions: isLoadingTeams || isLoadingPeriods,
  }), [
    organizationOptions,
    assessmentPeriodOptions,
    teamOptions,
    metricOptions,
    organizationFilterValues,
    assessmentPeriodFilter,
    teamFilterValues,
    metricIdFilter,
    isLoadingTeams,
    isLoadingPeriods,
  ]);

  const definitionsFilterConfig: DefinitionsFilterConfig = useMemo(() => ({
    metricTypeOptions: [...METRIC_TYPE_OPTIONS],
    statusOptions: [
      { key: 'all', text: 'All', value: undefined },
      { key: 'active', text: 'Active', value: true },
      { key: 'inactive', text: 'Inactive', value: false },
    ],
    metricTypeValue: metricTypeFilter,
    statusValue: isActiveFilter,
    onMetricTypeChange: setMetricTypeFilter,
    onStatusChange: setIsActiveFilter,
    onClearFilters: handleClearDefinitionsFilters,
  }), [metricTypeFilter, isActiveFilter]);


  const handleCreate = () => {
    setMetricToEdit(null);
    setIsFormOpen(true);
  };

  const handleEdit = (metric: MetricList | AssessmentPeriodMetricList) => {
    setMetricToEdit(metric);
    setIsFormOpen(true);
  };

  const handleDelete = (metric: MetricList | AssessmentPeriodMetricList) => {
    setMetricToDelete(metric);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!metricToDelete) return;
    
    try {
      if (activeTab === 'definitions') {
        await deleteMetric(metricToDelete.id).unwrap();
        addToast('Metric definition deleted successfully', { appearance: 'success' });
      } else {
        await deletePeriodMetric(metricToDelete.id).unwrap();
        addToast('Period metric value deleted successfully', { appearance: 'success' });
      }
      setIsDeleteConfirmOpen(false);
      setMetricToDelete(null);
      refetch();
    } catch (err: any) {
      console.error('Delete failed:', err);
      addToast('Failed to delete', { appearance: 'error' });
    }
  };

  const handleFormSuccess = () => {
    refetch();
    const message = activeTab === 'definitions' 
      ? (metricToEdit ? 'Metric definition updated successfully' : 'Metric definition created successfully')
      : (metricToEdit ? 'Period metric value updated successfully' : 'Period metric value created successfully');
    addToast(message, { appearance: 'success' });
  };

  const handleImportSuccess = () => {
    refetch();
    addToast('Metrics imported successfully', { appearance: 'success' });
  };

  const handleExport = async () => {
    try {
      let result;
      if (activeTab === 'definitions') {
        result = await exportMetrics({
          format: 'csv',
          params: {
            metric_type: metricTypeFilter,
            is_active: isActiveFilter,
          },
        }).unwrap();
      } else {
        result = await exportPeriodMetrics({
          format: 'csv',
          params: {
            assessment_period_id: assessmentPeriodFilter,
            metric_id: metricIdFilter,
            organization_id: organizationFilterValues,
            team_id: teamFilterValues,
          },
        }).unwrap();
      }
      
      // Handle blob download
      if (result instanceof Blob) {
        const url = window.URL.createObjectURL(result);
        const a = document.createElement('a');
        a.href = url;
        const filename = activeTab === 'definitions' 
          ? `metric_definitions_${new Date().toISOString().split('T')[0]}.csv`
          : `assessment_period_metrics_${new Date().toISOString().split('T')[0]}.csv`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        addToast('Export completed successfully', { appearance: 'success' });
      }
    } catch (err) {
      console.error('Export failed:', err);
      addToast('Failed to export', { appearance: 'error' });
    }
  };

  const controls = (
    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center' }}>
      <StyledAddButton
        onClick={handleCreate}
        label={activeTab === 'definitions' ? 'New Definition' : 'New Value'}
        width="140px"
        height="34px"
      />
      {isStaff && activeTab === 'values' && (
        <StyledAddButton
          onClick={() => setIsImportOpen(true)}
          label="Import CSV"
          width="120px"
          height="34px"
        />
      )}
      <Button
        onClick={handleExport}
        disabled={metrics.length === 0}
        icon="download"
        content="Export"
        style={{ height: '34px', minWidth: '120px', borderRadius: '8px' }}
      />
    </div>
  );

  return (
    <PageContent>
      <PageSection>


        {/* Tab Menu - visually separated card */}
        <div style={{ marginBottom: '18px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 0px',
          }}>
            <div>
              <Button.Group>
                {['Metric Definitions', 'Metric Values'].map((t, idx) => {
                  const tabColors: Array<any> = ['orange', 'blue'];
                  const color = tabColors[idx] || undefined;
                  const isActive = (idx === 0 && activeTab === 'definitions') || (idx === 1 && activeTab === 'values');
                  return (
                    <Button
                      key={t}
                      color={color}
                      basic={!isActive}
                      active={isActive}
                      onClick={() => setActiveTab(idx === 0 ? 'definitions' : 'values')}
                      style={{ paddingLeft: 10, paddingRight: 12 }}
                    >
                      {t}
                    </Button>
                  );
                })}
              </Button.Group>
            </div>

            {/* Header controls moved to the right side (mirrors MessagingMonitoring) */}
            <div style={{ minWidth: 180, textAlign: 'right', color: '#666', fontSize: 13 }}>
              {isLoading ? <LoadingSpinner text="Loading" showBg={false} /> : controls}
            </div>
          </div>
        </div>

        <MetricListComponent
          metrics={metrics}
          isLoading={isLoading}
          error={error}
          onEdit={handleEdit}
          onDelete={handleDelete}
          tabType={activeTab}
          valuesFilterConfig={activeTab === 'values' ? valuesFilterConfig : undefined}
          definitionsFilterConfig={activeTab === 'definitions' ? definitionsFilterConfig : undefined}
        />
      </PageSection>

      {/* Create/Edit Modal */}
      <MetricForm
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setMetricToEdit(null);
        }}
        onSuccess={handleFormSuccess}
        metricToEdit={metricToEdit}
        tabType={activeTab}
      />

      {/* Import Modal */}
      <MetricImport
        open={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onSuccess={handleImportSuccess}
      />

      {/* Delete Confirmation Modal */}
      <Modal open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} size="small">
        <Modal.Header>
          Delete {activeTab === 'definitions' ? 'Metric Definition' : 'Period Metric Value'}
        </Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to delete this {activeTab === 'definitions' ? 'metric definition' : 'period metric value'}?</p>
          {metricToDelete && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <strong>
                {'metric_name' in metricToDelete 
                  ? metricToDelete.metric_name 
                  : (metricToDelete as any).metric_name}
              </strong>
              {activeTab === 'values' && 'assessment_period_name' in metricToDelete && (
                <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
                  {metricToDelete.assessment_period_name || `Period ${(metricToDelete as any).assessment_period}`}
                  {'organization_name' in metricToDelete && metricToDelete.organization_name && ` • ${metricToDelete.organization_name}`}
                  {'team_name' in metricToDelete && metricToDelete.team_name && ` • ${metricToDelete.team_name}`}
                </div>
              )}
            </div>
          )}
          <p style={{ marginTop: '15px', color: '#d32f2f' }}>
            This action cannot be undone.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</Button>
          <Button negative onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Actions>
      </Modal>
    </PageContent>
  );
};

